import { useState, useEffect, useMemo } from "react";
import { Box, Button, Container, Group, Paper, Stack, Text, LoadingOverlay } from "@mantine/core";
import { IconArrowLeft, IconCheck } from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";
import { useNavigate, useParams } from "react-router-dom";
import NdaTemplate from "@/pages/nda/NdaTemplate";

interface NdaPageProps {
  embedded?: boolean;
  otherCompanyId?: string;
  collaboration?: any;
}

export default function NdaPage({
  embedded = false,
  otherCompanyId: propOtherCompanyId,
  collaboration: propCollaboration,
}: NdaPageProps) {
  const { userDetails, hasProfile } = useUserContext();
  const { id: paramOtherCompanyId } = useParams();
  const navigate = useNavigate();

  const otherCompanyId = embedded ? propOtherCompanyId : paramOtherCompanyId;

  // Loading states
  const [loading, setLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Collaboration state
  const [collaboration, setCollaboration] = useState<any>(propCollaboration);

  // NDA state
  const [ndaId, setNdaId] = useState<string | null>(null);

  // Other company data
  const [otherCompanyData, setOtherCompanyData] = useState<any>(null);

  // Company details
  const [company1Name, setCompany1Name] = useState("");
  const [company1Address, setCompany1Address] = useState("");
  const [company2Name, setCompany2Name] = useState("");
  const [company2Address, setCompany2Address] = useState("");

  /** After "I Accept" succeeds, stay on page until user clicks Continue (same destination as before: /collaborations). */
  const [showSignedConfirmation, setShowSignedConfirmation] = useState(false);

  // Derived values
  const matchId = collaboration?.id;

  const currentUserCompany = useMemo((): "company1" | "company2" => {
    if (!collaboration || !userDetails?.company?.id) return "company1";
    return collaboration.requesterCompanyId === userDetails.company.id ? "company1" : "company2";
  }, [collaboration?.requesterCompanyId, userDetails?.company?.id]);

  useEffect(() => {
    const fetchAllData = async () => {
      if (hasProfile === null) return;
      if (!userDetails?.company?.id || !otherCompanyId) {
        setIsLoadingData(false);
        return;
      }

      setIsLoadingData(true);

      try {
        const ndaContext = await trpc.nda.getNdaContext.query({
          companyId: otherCompanyId,
        });

        const collab = ndaContext.collaboration;
        setCollaboration(collab);
        setOtherCompanyData(ndaContext.otherCompany);

        const userRole = ndaContext.currentUserRole;

        if (userRole === "company1") {
          setCompany1Name(ndaContext.userCompany.legalName);
          setCompany1Address(ndaContext.userCompany.companyRegisteredAddress || "Registered address to be updated");
          setCompany2Name(ndaContext.otherCompany.legalName);
          setCompany2Address(
            ndaContext.otherCompany.additionalDetails?.companyRegisteredAddress || "Registered address to be updated"
          );
        } else {
          setCompany2Name(ndaContext.userCompany.legalName);
          setCompany2Address(ndaContext.userCompany.companyRegisteredAddress || "Registered address to be updated");
          setCompany1Name(ndaContext.otherCompany.legalName);
          setCompany1Address(
            ndaContext.otherCompany.additionalDetails?.companyRegisteredAddress || "Registered address to be updated"
          );
        }

        if (ndaContext.nda) {
          setNdaId(ndaContext.nda.id);

          if (ndaContext.nda.company1Name) setCompany1Name(ndaContext.nda.company1Name);
          if (ndaContext.nda.company1Address) setCompany1Address(ndaContext.nda.company1Address);
          if (ndaContext.nda.company2Name) setCompany2Name(ndaContext.nda.company2Name);
          if (ndaContext.nda.company2Address) setCompany2Address(ndaContext.nda.company2Address);
        }
      } catch (err) {
        console.error("Error fetching NDA context:", err);
        handleTrpcError(err);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchAllData();
  }, [otherCompanyId, userDetails?.company?.id, hasProfile]);

  // Derived state
  const currentUserAlreadyAccepted =
    currentUserCompany === "company1"
      ? !!collaboration?.requesterSignedNDA
      : !!collaboration?.receiverSignedNDA;

  const bothPartiesAccepted = !!collaboration?.requesterSignedNDA && !!collaboration?.receiverSignedNDA;

  const currentUserDataValid =
    currentUserCompany === "company1" ? !!company1Name && !!company1Address : !!company2Name && !!company2Address;

  const canSubmit = !!collaboration && currentUserDataValid;

  const handleCreateNda = async (): Promise<string | null> => {
    if (!matchId || !collaboration) return null;

    const company1Id = collaboration.requesterCompanyId;
    const company2Id = collaboration.receiverCompanyId;

    if (!company1Id || !company2Id) return null;

    setLoading(true);
    try {
      const nda = await trpc.nda.create.mutate({
        company1Id,
        company1Name: company1Name || "Pending",
        company1Address: company1Address || "Pending",
        company2Id,
        company2Name: company2Name || "Pending",
        company2Address: company2Address || "Pending",
        matchId,
      });

      setNdaId(nda.id);

      return nda.id;
    } catch (error) {
      handleTrpcError(error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptNda = async () => {
    if (currentUserAlreadyAccepted || !collaboration) return;

    setLoading(true);
    try {
      let currentNdaId = ndaId;

      // Check for existing NDA before creating
      if (!currentNdaId && matchId) {
        try {
          const existingNda = await trpc.nda.get.query({ matchId });
          if (existingNda) {
            currentNdaId = existingNda.id;
            setNdaId(existingNda.id);
          }
        } catch (err) {
          handleTrpcError(err);
        }
      }

      // Only create if NDA doesn't exist
      if (!currentNdaId) {
        currentNdaId = await handleCreateNda();
        if (!currentNdaId) {
          setLoading(false);
          return;
        }
      }

      await trpc.nda.accept.mutate({ ndaId: currentNdaId });

      // Reflect acceptance locally
      const updatedCollaboration = {
        ...collaboration,
        ...(currentUserCompany === "company1"
          ? { requesterSignedNDA: true }
          : { receiverSignedNDA: true }),
      };
      setCollaboration(updatedCollaboration);
      setShowSignedConfirmation(true);
    } catch (error) {
      handleTrpcError(error);
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (!collaboration) return "No Collaboration Found";
    if (currentUserAlreadyAccepted) return "Already Accepted";
    return "I Accept";
  };

  const getStatusMessage = () => {
    if (!collaboration) return "No active collaboration found with this company.";
    if (bothPartiesAccepted) return "NDA has been accepted by both parties.";
    if (currentUserAlreadyAccepted) return "Your acceptance has been recorded. Waiting for the other party.";
    return "Please accept the agreement to proceed.";
  };

  const handleContinueAfterSign = () => {
    navigate("/collaborations");
  };

  const Wrapper = embedded ? Box : Container;
  const wrapperProps = embedded ? {} : { fluid: true, px: "lg", py: "md" };

  return (
    <Box mih={embedded ? "auto" : "100vh"} pb={embedded ? 0 : 40}>
      {!embedded && (
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={16} />}
          size="sm"
          onClick={() => navigate("/requests")}
        >
          Back to Interests
        </Button>
      )}

      <Wrapper {...wrapperProps}>
        <Stack gap="xl">
          <Box pos="relative">
            <LoadingOverlay visible={isLoadingData} zIndex={100} overlayProps={{ radius: "sm", blur: 2 }} />

            <NdaTemplate
              company1Name={company1Name}
              company1Address={company1Address}
              company2Name={company2Name}
              company2Address={company2Address}
              matchId={matchId}
              company2Data={otherCompanyData}
              company1SignedInDb={!!collaboration?.requesterSignedNDA}
              company2SignedInDb={!!collaboration?.receiverSignedNDA}
              company1ShortName={
                currentUserCompany === "company1"
                  ? userDetails?.company?.tradeName || company1Name
                  : otherCompanyData?.tradeName || company1Name
              }
              company2ShortName={
                currentUserCompany === "company2"
                  ? userDetails?.company?.tradeName || company2Name
                  : otherCompanyData?.tradeName || company2Name
              }
              company1IncorporationLaw={
                currentUserCompany === "company1"
                  ? userDetails?.company?.headquartersCountry || "___"
                  : otherCompanyData?.headquartersCountry || "___"
              }
              company2IncorporationLaw={
                currentUserCompany === "company2"
                  ? userDetails?.company?.headquartersCountry || "___"
                  : otherCompanyData?.headquartersCountry || "___"
              }
              governingLaw="Singapore"
              platformName="GTsetu"
              platformUrl="https://gtsetu.com"
              currentUserCompany={currentUserCompany}
              showAllDetails={embedded}
            />
          </Box>

          <Paper p="lg" radius="md" withBorder>
            <Group justify="space-between" align="center" wrap="wrap">
              {showSignedConfirmation ? (
                <>
                  <Text size="sm" fw={500} c="green.7">
                    Your NDA has been signed.
                  </Text>
                  <Button size="lg" onClick={handleContinueAfterSign}>
                    Continue
                  </Button>
                </>
              ) : (
                <>
                  <Text size="sm" c="slate.6">
                    {getStatusMessage()}
                  </Text>

                  {!currentUserAlreadyAccepted && (
                    <Button
                      size="lg"
                      onClick={handleAcceptNda}
                      loading={loading}
                      disabled={!canSubmit}
                      leftSection={<IconCheck size={20} />}
                    >
                      {getButtonText()}
                    </Button>
                  )}
                </>
              )}
            </Group>
          </Paper>
        </Stack>
      </Wrapper>
    </Box>
  );
}
